/**
 * DateRangePicker — EXL-672
 *
 * Date range picker for the SearchFilters sidebar.
 *
 * Features:
 *   - Preset buttons: Last 7 days, Last 30 days, Last 90 days, Last year, Custom range
 *   - Custom range: dual text inputs for start/end date (YYYY-MM-DD)
 *   - Open-ended ranges: "After [date]" sets dateFrom only; "Before [date]" sets dateTo only
 *   - Date format in UI: "MMM DD, YYYY"
 *   - Constraints: min = 2020-01-01, max = today; start must be before end
 *   - Auto-closes on preset selection; requires "Apply" for custom range
 *   - Inline validation: "Start date must be before end date"
 */

import { useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateRange {
  from: string;  // ISO YYYY-MM-DD or ''
  to: string;    // ISO YYYY-MM-DD or ''
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_DATE = '2020-01-01';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatDisplayDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

type PresetKey = '7d' | '30d' | '90d' | '1y' | 'custom' | '';

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: '7d',  label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: '1y',  label: 'Last year' },
  { key: 'custom', label: 'Custom range' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const today = todayISO();

  // Detect which preset matches current value
  const detectPreset = useCallback((): PresetKey => {
    if (!value.from && !value.to) return '';
    if (value.from === daysAgoISO(7) && value.to === today) return '7d';
    if (value.from === daysAgoISO(30) && value.to === today) return '30d';
    if (value.from === daysAgoISO(90) && value.to === today) return '90d';
    if (value.from === daysAgoISO(365) && value.to === today) return '1y';
    return 'custom';
  }, [value, today]);

  const [activePreset, setActivePreset] = useState<PresetKey>(detectPreset);
  const [customFrom, setCustomFrom] = useState(value.from);
  const [customTo, setCustomTo] = useState(value.to);
  const [customError, setCustomError] = useState('');

  const handlePreset = (key: PresetKey) => {
    if (key === 'custom') {
      setActivePreset('custom');
      // Pre-fill custom inputs from current value
      setCustomFrom(value.from);
      setCustomTo(value.to);
      setCustomError('');
      return;
    }

    setActivePreset(key);
    setCustomError('');

    const from7  = daysAgoISO(7);
    const from30 = daysAgoISO(30);
    const from90 = daysAgoISO(90);
    const from1y = daysAgoISO(365);

    const rangeMap: Record<Exclude<PresetKey, 'custom' | ''>, DateRange> = {
      '7d':  { from: from7,  to: today },
      '30d': { from: from30, to: today },
      '90d': { from: from90, to: today },
      '1y':  { from: from1y, to: today },
    };

    onChange(rangeMap[key as Exclude<PresetKey, 'custom' | ''>]);
  };

  const handleClearPreset = () => {
    setActivePreset('');
    setCustomFrom('');
    setCustomTo('');
    setCustomError('');
    onChange({ from: '', to: '' });
  };

  const handleApplyCustom = () => {
    // Validate
    if (customFrom && customTo && customFrom > customTo) {
      setCustomError('Start date must be before end date');
      return;
    }
    if (customFrom && customFrom < MIN_DATE) {
      setCustomError(`Start date cannot be before ${formatDisplayDate(MIN_DATE)}`);
      return;
    }
    if (customTo && customTo > today) {
      setCustomError(`End date cannot be after today (${formatDisplayDate(today)})`);
      return;
    }
    setCustomError('');
    onChange({ from: customFrom, to: customTo });
  };

  const hasActiveRange = value.from || value.to;

  return (
    <div className="space-y-2">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => handlePreset(key)}
            className={`px-2.5 py-1 text-xs rounded border transition-colors ${
              activePreset === key
                ? 'bg-neutral-900 text-white border-neutral-900'
                : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500 hover:bg-neutral-50'
            }`}
          >
            {label}
          </button>
        ))}
        {hasActiveRange && (
          <button
            type="button"
            onClick={handleClearPreset}
            className="px-2.5 py-1 text-xs rounded border border-red-200 text-red-600
              hover:bg-red-50 transition-colors"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Active preset display */}
      {hasActiveRange && activePreset !== 'custom' && (
        <p className="text-xs text-neutral-500">
          {value.from && !value.to && `After ${formatDisplayDate(value.from)}`}
          {!value.from && value.to && `Before ${formatDisplayDate(value.to)}`}
          {value.from && value.to && `${formatDisplayDate(value.from)} – ${formatDisplayDate(value.to)}`}
        </p>
      )}

      {/* Custom range inputs */}
      {activePreset === 'custom' && (
        <div className="space-y-2 p-3 bg-neutral-50 rounded border border-neutral-200">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                From
              </label>
              <input
                type="date"
                value={customFrom}
                min={MIN_DATE}
                max={customTo || today}
                onChange={(e) => { setCustomFrom(e.target.value); setCustomError(''); }}
                className="w-full px-2 py-1.5 text-xs border border-neutral-300 rounded
                  focus:outline-none focus:ring-1 focus:ring-neutral-900/20 focus:border-neutral-500"
                aria-label="Start date"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                To
              </label>
              <input
                type="date"
                value={customTo}
                min={customFrom || MIN_DATE}
                max={today}
                onChange={(e) => { setCustomTo(e.target.value); setCustomError(''); }}
                className="w-full px-2 py-1.5 text-xs border border-neutral-300 rounded
                  focus:outline-none focus:ring-1 focus:ring-neutral-900/20 focus:border-neutral-500"
                aria-label="End date"
              />
            </div>
          </div>

          {customError && (
            <p className="text-xs text-red-600" role="alert">{customError}</p>
          )}

          <button
            type="button"
            onClick={handleApplyCustom}
            disabled={!customFrom && !customTo}
            className="w-full px-3 py-1.5 text-xs font-medium bg-neutral-900 text-white
              rounded hover:bg-neutral-700 transition-colors disabled:opacity-40
              disabled:cursor-not-allowed"
          >
            Apply date range
          </button>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
